import { useCallback, useMemo, useState } from "react";

const CLOSED = { show: false };

/**
 * State plumbing for a one-off confirmation dialog.
 *
 * A screen with several confirmable actions otherwise grows a parallel useState for
 * every knob (text, labels, variant, the pending callback) plus a reset for the ones
 * the last caller left behind. This keeps the whole dialog description in a single
 * object, so each action passes exactly what it needs and nothing leaks into the next.
 *
 *   const { confirm, confirmationProps } = useConfirmation();
 *   ...
 *   <button onClick={() => confirm({
 *       title: "Cancel this booking?",
 *       variant: "danger",
 *       confirmLabel: "Cancel Booking",
 *       onConfirm: () => cancelBooking(id),
 *   })} />
 *   ...
 *   <ConfirmationModal {...confirmationProps} />
 *
 * The dialog closes before `onConfirm` runs, so an action that shows its own loading
 * overlay or reloads the page is not left sitting behind an open modal. An action that
 * needs the dialog to stay up while it works (an inline spinner, an in-dialog error)
 * should own its own state and render ConfirmationModal directly.
 */
export const useConfirmation = () => {
    const [request, setRequest] = useState(CLOSED);

    const confirm = useCallback((options) => setRequest({ ...options, show: true }), []);
    const close = useCallback(() => setRequest(CLOSED), []);

    const confirmationProps = useMemo(
        () => ({
            ...request,
            onCancel: close,
            onConfirm: () => {
                close();
                request.onConfirm?.();
            },
        }),
        [request, close]
    );

    return { confirm, close, confirmationProps };
};

export default useConfirmation;
